// Curated event calendar — 3 events per week for the next 4 weeks, rolling from today

// ---------- Date helpers ----------
const _addDays = (d, n) => {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
};
const _iso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};
const _nextWeekday = (from, dow) => {
    // dow: 0=Sun .. 6=Sat
    const r = new Date(from);
    r.setHours(0, 0, 0, 0);
    const diff = (dow - r.getDay() + 7) % 7;
    r.setDate(r.getDate() + diff);
    return r;
};

const _today = new Date();
_today.setHours(0, 0, 0, 0);

// Pick 12 random, unique, upcoming dates within the next 28 days, sorted ascending.
const _pickRandomUpcomingDates = (count, windowDays) => {
    const pool = [];
    for (let i = 1; i <= windowDays; i++) pool.push(_addDays(_today, i));
    // Fisher–Yates shuffle
    for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    return pool
        .slice(0, count)
        .sort((a, b) => a - b)
        .map(_iso);
};

const _dates = _pickRandomUpcomingDates(12, 28);

// Seats: random 150–300 per event, computed once at module load
const _seats = () => Math.floor(Math.random() * (300 - 150 + 1)) + 150;

// ---------- Signature Experiences ----------
// Each event includes one signature experience hosted at the property with
// licensed professionals and certified guides onboard for every guest.
const _EXP = {
    "deep-sea": {
        key: "deep-sea",
        name: "Deep Sea Fishing",
        tagline: "Pacific charter departing Marina del Rey",
        description:
            "Guests are flown by private shuttle to a 58' sport-fisher waiting at Marina del Rey. USCG-licensed captains and IGFA-certified mates prepare conventional and stand-up gear for yellowfin, yellowtail, and dorado. Each party of four is paired with a dedicated mate who rigs live bait, coaches technique, and fillets the day's catch for the return flight — delivered to guests' chef on arrival.",
        gear: "All tackle, bait, licenses, foul-weather gear, and Dramamine provided.",
        professionals: [
            { role: "Captain", name: "Capt. Reese Callahan", cred: "USCG 100-Ton Master · 28 yrs" },
            { role: "Head Mate", name: "Santiago Ruiz", cred: "IGFA-Certified · Trilingual" },
            { role: "Onboard Medic", name: "Nurse Amelia Harper, RN", cred: "Wilderness First Responder" },
        ],
    },
    "haunted": {
        key: "haunted",
        name: "Haunted Heritage Tour",
        tagline: "After-dark exploration of Old Vegas and the Redd Estate",
        description:
            "A lantern-lit caravan from the estate to three of Southern Nevada's documented paranormal sites — the Redd Mansion, the Vegas Hotel Apache, and the Goldfield's Room 109 — each with recorded EVPs and signed historian logs. Guests are accompanied by a parapsychologist, a certified historian, and a licensed private security detail. EMF meters, thermal cameras, and archival photography provided.",
        gear: "Thermal imagers, EMF meters, velvet cloaks, hand-warmers, and a private archive dossier for each guest.",
        professionals: [
            { role: "Lead Historian", name: "Dr. Evangeline Thorne", cred: "PhD Nevada History · UNLV" },
            { role: "Parapsychologist", name: "Dr. Silas Orrick", cred: "Certified Paranormal Investigator" },
            { role: "Security Lead", name: "Col. Rafael Vega (Ret.)", cred: "Licensed PI · Armed Escort" },
        ],
    },
    "heli": {
        key: "heli",
        name: "Helicopter Tours",
        tagline: "Private rotor tour of the Strip and Red Rock Canyon",
        description:
            "Two Airbus EC130s depart the estate helipad for a 40-minute scenic loop over the Las Vegas Strip, Hoover Dam, and Red Rock Canyon. FAA-licensed commercial pilots, champagne service aloft, and noise-canceling Bose headsets per seat. Guests choose between a sunset Strip route or a Red Rock canyon traverse at golden hour. Vertical touchdown returns them to the estate without interrupting the reception.",
        gear: "Noise-canceling headsets, champagne service, optional GoPro record of your flight.",
        professionals: [
            { role: "Command Pilot", name: "Capt. Marcus Hale", cred: "FAA ATP Rotorcraft · 6,400 hrs" },
            { role: "Second-in-Command", name: "Fo. Naomi Wren", cred: "FAA Commercial · 2,100 hrs" },
            { role: "Flight Concierge", name: "Olivia Marchetti", cred: "Certified Flight Attendant" },
        ],
    },
    "balloon": {
        key: "balloon",
        name: "Hot Air Balloon Tours",
        tagline: "Sunrise ascension over Red Rock Canyon",
        description:
            "A private fleet of three 8-passenger envelopes launches from a dry-bed strip thirty minutes from the estate. Guests are shuttled in advance, briefed by the aeronaut, and rise with the desert sunrise for a 60–90 minute drift over Red Rock Canyon. Traditional Champagne-and-plaque landing ceremony on touchdown, followed by a seated Provençal breakfast in the desert.",
        gear: "Morning thermals, cashmere wraps, champagne, and aeronaut-signed Montgolfier certificates.",
        professionals: [
            { role: "Lead Aeronaut", name: "Capt. Isadora Vance", cred: "FAA LTA · 12 yrs commercial" },
            { role: "Chase Team Lead", name: "Benji Moretti", cred: "Advanced First Aid · Recovery Ops" },
            { role: "Weather Officer", name: "Dr. Paul Reinholt", cred: "AMS Certified Broadcast Meteorologist" },
        ],
    },
    "kayak": {
        key: "kayak",
        name: "Kayaking",
        tagline: "Sunrise paddle on Lake Las Vegas and the Black Canyon",
        description:
            "A guided flat-water paddle on Lake Las Vegas or an emerald-water traverse of Black Canyon below Hoover Dam. Tandem sea kayaks, dry-bags, and lightweight PFDs are fitted for every guest. Two ACA-certified guides lead every pod of six; a safety boat shadows the group. Novices receive a 15-minute technique brief on-shore; experienced paddlers are offered the canyon route.",
        gear: "Kayaks, PFDs, dry bags, reef-safe sunscreen, hydration, and a shoreline brunch spread at turnaround.",
        professionals: [
            { role: "Lead Guide", name: "Sienna Brooks", cred: "ACA L4 Coastal Kayak Instructor" },
            { role: "Assistant Guide", name: "Tomás Delgado", cred: "ACA L3 · Swiftwater Rescue" },
            { role: "Safety Boat Pilot", name: "Capt. Kira Olsen", cred: "USCG Master 50-Ton" },
        ],
    },
    "museum": {
        key: "museum",
        name: "Museums",
        tagline: "Private after-hours curator tour",
        description:
            "A private after-hours opening of The Neon Museum's Boneyard and a curator-led tour of the Nevada Museum of Art's current photography show. Each guest pod of eight is accompanied by a named curator, an archivist, and a docent. Cocktails served in the North Gallery; optional printing of a souvenir Neon Museum photograph in the on-site darkroom.",
        gear: "White-glove archive access, printed provenance notes, and a take-home catalogue signed by the curator.",
        professionals: [
            { role: "Head Curator", name: "Dr. Minerva Lark", cred: "PhD Art History · 20 yrs" },
            { role: "Archivist", name: "Claude Beaumont", cred: "Certified Archivist (SAA)" },
            { role: "Docent", name: "Harriet Ozawa", cred: "Accredited Museum Educator" },
        ],
    },
    "paintball": {
        key: "paintball",
        name: "Paintballing",
        tagline: "Principals-only speedball & scenario course",
        description:
            "A 14-acre purpose-built course at the estate's south ridge: two speedball arenas, a scenario town, and a woodsball gully. Electronic markers, tournament-grade paint, and chest-mounted telemetry provide live scoreboards. A team of certified refs and safety marshals runs every match; medics stand by on two ATVs. Optional classroom briefing on tactical movement by a former SF instructor.",
        gear: "Planet Eclipse markers, full pod harness, chest protector, helmet, gloves, 500-paint kit per guest.",
        professionals: [
            { role: "Tactical Instructor", name: "Sgt. Major Dane Kovalchuk (Ret.)", cred: "US Army SF · 22 yrs" },
            { role: "Head Referee", name: "Lillian Foster", cred: "NXL Certified Official" },
            { role: "Field Medic", name: "EMT Jordan Reyes", cred: "NREMT · TCCC Certified" },
        ],
    },
    "parasail": {
        key: "parasail",
        name: "Parasailing",
        tagline: "800-foot ascent over Lake Mead",
        description:
            "Private WSIA-inspected winch boats depart the Lake Mead Marina with guests flying 600–800 feet above the water on tandem or triple chutes. USCG-licensed captains, certified winch operators, and a full-time water-safety swimmer accompany every flight. Champagne flutes on the swim-step upon descent; GoPro flight footage delivered to each guest by end of day.",
        gear: "WSIA-inspected harnesses, PFDs, GoPro recording, sunscreen, and a post-flight poolside reception.",
        professionals: [
            { role: "Boat Captain", name: "Capt. Dmitri Volkov", cred: "USCG Master 100-Ton" },
            { role: "Winch Operator", name: "Hannah Kincaid", cred: "WSIA Certified · 9 yrs" },
            { role: "Safety Swimmer", name: "Jace Okafor", cred: "PADI Divemaster · Lifeguard USLA" },
        ],
    },
};

// Each event gets one signature experience (cycled across the 8 options)
const _EXP_ORDER = [
    "balloon",   // evt-01 · sunset estate showing
    "museum",    // evt-02 · family office briefing
    "heli",      // evt-03 · gala
    "haunted",   // evt-04 · Ascaya after dark
    "paintball", // evt-05 · principals roundtable
    "kayak",     // evt-06 · Saturday collectors tour
    "parasail",  // evt-07 · hospitality forum
    "deep-sea",  // evt-08 · cigar & cabernet
    "museum",    // evt-09 · architecture symposium
    "heli",      // evt-10 · solstice salon
    "paintball", // evt-11 · 1031 workshop
    "balloon",   // evt-12 · auction preview
];

// ---------- Event content (dates and capacities are injected below) ----------
const _eventContent = [
    {
        id: "evt-01",
        time: "18:30",
        endTime: "21:30",
        title: "The Ridges Sunset — Private Estate Showing",
        type: "showing",
        location: "Obsidian House · The Ridges, Las Vegas",
        hero: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1600&q=80",
        status: "By Invitation",
        description:
            "An intimate twilight walkthrough of the $9.75M Obsidian House — a minimalist architectural monolith in honed black basalt. Chef-curated small plates by Chef Marco Loretti, a 2005 Krug pairing, and a private Q&A with the architect on design provenance and material sourcing.",
        activities: [
            "Architect-led private walkthrough of all three wings",
            "Champagne reception in the two-story great room",
            "Chef's tasting menu by Chef Marco Loretti (5 courses)",
            "Live jazz trio on the rooftop observatory",
            "Discreet buyer Q&A with listing partners",
        ],
        speakers: [
            {
                name: "Alessandra Monroe",
                title: "Managing Principal, REA Inc",
                image: "https://images.pexels.com/photos/7414956/pexels-photo-7414956.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=500",
            },
            {
                name: "Hiroshi Tanaka",
                title: "Principal Architect, Tanaka / Bell",
                image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=500&q=80",
            },
        ],
        sponsors: [
            { name: "Krug Champagne", tier: "Presenting" },
            { name: "Davidoff of Geneva", tier: "Hospitality" },
            { name: "Rolls-Royce Motor Cars Las Vegas", tier: "Arrival" },
        ],
    },
    {
        id: "evt-02",
        time: "08:00",
        endTime: "10:30",
        title: "Las Vegas CRE Outlook — Family Office Briefing",
        type: "briefing",
        location: "The Four Seasons Las Vegas · Presidential Suite",
        hero: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1600&q=80",
        status: "By Application",
        description:
            "A closed-door breakfast briefing for qualified family offices and single-investor funds on the 2026 Las Vegas commercial real-estate thesis. Underwriting deep-dive on office, industrial, hospitality, and medical-office sub-markets with post-presentation one-on-ones.",
        activities: [
            "Continental breakfast service, 8:00–8:30",
            "Market presentation & 2026 thesis (45 min)",
            "Panel: underwriting in a structurally higher-rate environment",
            "Off-market opportunity set walkthrough (NDA required)",
            "Private 15-minute one-on-one breakouts",
        ],
        speakers: [
            {
                name: "Julian Ashford",
                title: "Partner, Commercial Capital Markets",
                image: "https://images.unsplash.com/photo-1621973133111-63649acea55a?auto=format&fit=crop&w=500&q=80",
            },
            {
                name: "Dr. Priya Ramaswamy",
                title: "Chief Economist, Meridian Research",
                image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=500&q=80",
            },
            {
                name: "Marcus DuBois",
                title: "Head of Industrial & Logistics",
                image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=500&q=80",
            },
        ],
        sponsors: [
            { name: "JP Morgan Private Bank", tier: "Presenting" },
            { name: "Chubb Private Client", tier: "Strategic" },
            { name: "Four Seasons Las Vegas", tier: "Venue" },
        ],
    },
    {
        id: "evt-03",
        time: "19:00",
        endTime: "23:00",
        title: "Gilt Gala — Annual Client Benefit",
        type: "gala",
        location: "Wynn Las Vegas · Garden Ballroom",
        hero: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1600&q=80",
        status: "Black Tie",
        description:
            "Our flagship annual gala honoring the firm's clients, partners, and a rotating philanthropic cause. This year's proceeds support the Nevada Veterans Housing Initiative. Champagne reception, six-course seated dinner, and a silent auction of unique experiences curated from our global partner network.",
        activities: [
            "Arrival & champagne reception in the foyer",
            "Six-course plated dinner by Chef Theo Randall",
            "Silent auction: private-jet weekends, Riva cruises, Michelin pop-ups",
            "Live performance by the Vegas Philharmonic String Quartet",
            "Midnight brandy & cigar room",
        ],
        speakers: [
            {
                name: "Alessandra Monroe",
                title: "Managing Principal, REA Inc — Host",
                image: "https://images.pexels.com/photos/7414956/pexels-photo-7414956.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=500",
            },
            {
                name: "Col. Rafael Vega (Ret.)",
                title: "Chairman, Nevada Veterans Housing Initiative",
                image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=500&q=80",
            },
        ],
        sponsors: [
            { name: "Patek Philippe", tier: "Presenting" },
            { name: "Veuve Clicquot La Grande Dame", tier: "Champagne" },
            { name: "NetJets", tier: "Arrival" },
            { name: "Bentley Las Vegas", tier: "Mobility" },
        ],
    },
    {
        id: "evt-04",
        time: "17:00",
        endTime: "20:00",
        title: "Ascaya After Dark — Architect Studio Tour",
        type: "showing",
        location: "The Desert Pavilion · Ascaya, Henderson",
        hero: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1600&q=80",
        status: "RSVP Required",
        description:
            "Witness a $12.2M glass residence as the desert sun falls behind the McCullough Range. Floor-to-ceiling walls disappear; the cantilevered pool deck glows. Oaxacan chef José Mendoza prepares a mezcal-forward tasting menu while the homeowner's collection of Bauhaus ceramics opens to private viewing.",
        activities: [
            "Self-guided architectural tour (with printed monograph)",
            "Mezcal & agave tasting on the pool deck",
            "Five-course Oaxacan tasting by Chef José Mendoza",
            "Private viewing: Bauhaus ceramics collection",
            "Post-dinner stargazing with astronomer Dr. Iris Chen",
        ],
        speakers: [
            {
                name: "Nadia Kovač",
                title: "Director — Luxury Residential",
                image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=500&q=80",
            },
            {
                name: "Dr. Iris Chen",
                title: "Astronomer, Mt. Charleston Observatory",
                image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=500&q=80",
            },
        ],
        sponsors: [
            { name: "Del Maguey Single Village Mezcal", tier: "Presenting" },
            { name: "Hermès Maison", tier: "Design Partner" },
            { name: "Porsche Las Vegas", tier: "Mobility" },
        ],
    },
    {
        id: "evt-05",
        time: "10:00",
        endTime: "14:00",
        title: "Industrial Capital Roundtable",
        type: "roundtable",
        location: "REA Inc Boardroom · 2685 S Rainbow Blvd",
        hero: "https://images.unsplash.com/photo-1565793298595-6a879b1d9492?auto=format&fit=crop&w=1600&q=80",
        status: "Closed Door — Principals Only",
        description:
            "A working session for principals active in Southern Nevada industrial and last-mile logistics. Live underwriting of three off-market portfolios totaling 2.1M sq ft. Confidential, principals-only, catered working lunch. Expect to exchange term sheets, not business cards.",
        activities: [
            "Live underwriting of three off-market portfolios",
            "Rent-growth & cap-rate deep-dive for North LV submarket",
            "Plated working lunch by Chef Laurent Dumas",
            "Signed-NDA access to the firm's shadow pipeline",
            "Post-session 1:1 principal breakouts",
        ],
        speakers: [
            {
                name: "Marcus DuBois",
                title: "Head of Industrial & Logistics, REA Inc",
                image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=500&q=80",
            },
            {
                name: "Evelyn Park",
                title: "Managing Director, Prologis Capital",
                image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=500&q=80",
            },
        ],
        sponsors: [
            { name: "CBRE Debt & Structured Finance", tier: "Presenting" },
            { name: "Marsh Private Client", tier: "Strategic" },
        ],
    },
    {
        id: "evt-06",
        time: "14:00",
        endTime: "17:00",
        title: "Maison Noir — Saturday Collector's Tour",
        type: "showing",
        location: "Maison Noir · Queensridge, Las Vegas",
        hero: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1600&q=80",
        status: "RSVP Required",
        description:
            "French-contemporary estate on Badlands Golf. Tour the 8,100 sq ft residence, the wellness spa wing, and a private gallery featuring on-loan works by Soulages and Hartung, curated in partnership with Galerie Perrotin.",
        activities: [
            "Three-zone private walkthrough (house, spa wing, gallery)",
            "Champagne brunch on the motor court lawn",
            "Curator-led salon: Soulages & the monochrome tradition",
            "Golf-course vista cocktail hour at 4pm",
            "Optional test-drive courtesy of McLaren Las Vegas",
        ],
        speakers: [
            {
                name: "Ethan Calloway",
                title: "Advisor — Family Office Services",
                image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=500&q=80",
            },
            {
                name: "Claire Devereaux",
                title: "Curator, Galerie Perrotin (Paris)",
                image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=500&q=80",
            },
        ],
        sponsors: [
            { name: "Galerie Perrotin", tier: "Cultural Partner" },
            { name: "McLaren Las Vegas", tier: "Mobility" },
            { name: "Ruinart Blanc de Blancs", tier: "Champagne" },
        ],
    },
    {
        id: "evt-07",
        time: "11:00",
        endTime: "13:30",
        title: "Trophy Hospitality Forum",
        type: "summit",
        location: "Bellagio · Tuscany Kitchen",
        hero: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1600&q=80",
        status: "By Invitation",
        description:
            "A principals forum on branded hospitality and the future of trophy hotel assets on the Strip. Case studies on recent flag conversions, branded-residence economics, and a confidential preview of a $185M off-market boutique hotel opportunity.",
        activities: [
            "Seated luncheon by Bellagio Executive Chef",
            "Panel: the next decade of the Las Vegas Strip",
            "Case study: Auric Hotel conversion pro-forma",
            "Off-market tour (confidential, buses depart at 1:00pm)",
        ],
        speakers: [
            {
                name: "Ava St. Claire",
                title: "Global Head, Luxury Hospitality — Bain & Co.",
                image: "https://images.pexels.com/photos/7414956/pexels-photo-7414956.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=500",
            },
            {
                name: "Julian Ashford",
                title: "Partner, REA Inc",
                image: "https://images.unsplash.com/photo-1621973133111-63649acea55a?auto=format&fit=crop&w=500&q=80",
            },
            {
                name: "Vikram Chandra",
                title: "Managing Director, Starwood Capital",
                image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=500&q=80",
            },
        ],
        sponsors: [
            { name: "Marriott International — Luxury Group", tier: "Presenting" },
            { name: "Dom Pérignon", tier: "Hospitality" },
        ],
    },
    {
        id: "evt-08",
        time: "19:00",
        endTime: "22:30",
        title: "Cigar & Cabernet — Partners' Table",
        type: "tasting",
        location: "Villa di Oro · MacDonald Highlands",
        hero: "https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=1600&q=80",
        status: "By Invitation",
        description:
            "An intimate evening at the firm's $24.9M Villa di Oro. Hand-rolled Cohibas from a private 1992 Habanos reserve paired with a vertical tasting of Opus One from the estate's cellar.",
        activities: [
            "Villa tour with historian Dr. Marco Conti",
            "Opus One vertical tasting (2005 → 2018)",
            "Private Habanos sommelier experience",
            "Tasting menu by Michelin-starred Chef Paolo Ricci",
            "Fireside digestif service with 1957 Armagnac",
        ],
        speakers: [
            {
                name: "Alessandra Monroe",
                title: "Managing Principal, REA Inc",
                image: "https://images.pexels.com/photos/7414956/pexels-photo-7414956.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=500",
            },
            {
                name: "Dr. Marco Conti",
                title: "Historian, Italian Renaissance Estates",
                image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=500&q=80",
            },
        ],
        sponsors: [
            { name: "Opus One Winery", tier: "Presenting" },
            { name: "Habanos S.A.", tier: "Cigar Partner" },
            { name: "Aston Martin Las Vegas", tier: "Arrival" },
        ],
    },
    {
        id: "evt-09",
        time: "15:00",
        endTime: "18:00",
        title: "Architecture & Provenance Symposium",
        type: "symposium",
        location: "Smith Center for the Performing Arts · Boman Pavilion",
        hero: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=80",
        status: "Public (Ticketed)",
        description:
            "Our annual public-facing symposium on luxury residential architecture, preservation, and provenance. Three keynotes, a panel on adaptive-reuse in the American West, and a closing reception on the Reynolds Hall terrace.",
        activities: [
            "Keynote: 'The Quiet Monument' — Hiroshi Tanaka",
            "Keynote: 'Provenance as Asset Class' — Sir Nicholas Wren",
            "Panel: adaptive reuse & the American desert vernacular",
            "Closing reception with views of the Strip at dusk",
        ],
        speakers: [
            {
                name: "Hiroshi Tanaka",
                title: "Principal Architect, Tanaka / Bell (Kyoto)",
                image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=500&q=80",
            },
            {
                name: "Sir Nicholas Wren",
                title: "Chairman, Wren Heritage Foundation (London)",
                image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=500&q=80",
            },
            {
                name: "Serena Ito",
                title: "Director — Administration, REA Inc",
                image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=500&q=80",
            },
        ],
        sponsors: [
            { name: "Architectural Digest", tier: "Presenting" },
            { name: "Christie's International Real Estate", tier: "Strategic" },
            { name: "The Smith Center", tier: "Venue" },
        ],
    },
    {
        id: "evt-10",
        time: "18:00",
        endTime: "21:00",
        title: "Solstice Salon — Client Reception",
        type: "gala",
        location: "Sky Loft at The Martin · Full-Floor Penthouse",
        hero: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80",
        status: "Client & Guest",
        description:
            "A 360° view of the Las Vegas Strip from a $6.45M penthouse, wrapped in candlelight. Mulled Burgundy, oyster bar, and a chamber performance of Vivaldi's 'Winter' by the Las Vegas Philharmonic.",
        activities: [
            "Arrival champagne on the wrap-around terrace",
            "Penthouse tour (full-floor)",
            "Oyster bar & canapés by Chef Amélie Rousseau",
            "Vivaldi's 'Winter' — LV Philharmonic String Quintet",
            "Midnight hot-toddy service & fireworks viewing",
        ],
        speakers: [
            {
                name: "Nadia Kovač",
                title: "Director — Luxury Residential",
                image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=500&q=80",
            },
            {
                name: "Maestro Luca Rinaldi",
                title: "Guest Conductor, LV Philharmonic",
                image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=500&q=80",
            },
        ],
        sponsors: [
            { name: "Louis Roederer Cristal", tier: "Champagne" },
            { name: "Baccarat Maison", tier: "Host Gifting" },
            { name: "Four Seasons Private Residences", tier: "Strategic" },
        ],
    },
    {
        id: "evt-11",
        time: "10:00",
        endTime: "13:00",
        title: "Year-End 1031 Exchange Workshop",
        type: "briefing",
        location: "REA Inc Private Boardroom · 2685 S Rainbow Blvd",
        hero: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1600&q=80",
        status: "RSVP Required",
        description:
            "A working session for investors planning 1031 exchanges next quarter. Tax counsel, replacement-property sourcing, and a confidential brief of the firm's current ID-ready inventory across office, medical, and industrial.",
        activities: [
            "Tax-deferred exchange mechanics (45 min)",
            "Qualified-intermediary structuring",
            "Live walkthrough of ID-ready inventory",
            "Working lunch with one-on-one counsel sessions",
        ],
        speakers: [
            {
                name: "Marcus DuBois",
                title: "Head of Industrial & Logistics",
                image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=500&q=80",
            },
            {
                name: "Rebecca Stein, Esq.",
                title: "Partner, Latham & Watkins Tax Group",
                image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=500&q=80",
            },
        ],
        sponsors: [
            { name: "Latham & Watkins LLP", tier: "Presenting" },
            { name: "IPX1031 Exchange Services", tier: "Strategic" },
        ],
    },
    {
        id: "evt-12",
        time: "16:00",
        endTime: "19:00",
        title: "Trophy Auction Preview",
        type: "auction",
        location: "REA Inc Private Showroom · 2685 S Rainbow Blvd",
        hero: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1600&q=80",
        status: "By Invitation",
        description:
            "Preview the firm's upcoming trophy offering — a curated silent portfolio of six estates and two commercial assets not yet on-market, including a Strip-adjacent hospitality trophy. Dossiers under strict NDA.",
        activities: [
            "Private dossier review with partners",
            "Silent room: 8 trophy offerings, by invitation",
            "Cocktail hour with Dom Pérignon Plénitude 2",
            "NDA signing & pre-registration for bidding",
        ],
        speakers: [
            {
                name: "Alessandra Monroe",
                title: "Managing Principal, REA Inc",
                image: "https://images.pexels.com/photos/7414956/pexels-photo-7414956.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=500",
            },
            {
                name: "Julian Ashford",
                title: "Partner, Commercial Capital Markets",
                image: "https://images.unsplash.com/photo-1621973133111-63649acea55a?auto=format&fit=crop&w=500&q=80",
            },
        ],
        sponsors: [
            { name: "Sotheby's International Realty", tier: "Strategic" },
            { name: "Dom Pérignon", tier: "Champagne" },
            { name: "Bugatti Las Vegas", tier: "Arrival" },
        ],
    },
];

// Inject rolling dates + random capacities (seats 150–300) + signature experience
export const events = _eventContent.map((e, i) => {
    const capacity = _seats();
    const taken = Math.floor(Math.random() * Math.max(1, capacity - 10));
    const experience = _EXP[_EXP_ORDER[i]];
    return {
        ...e,
        date: _dates[i],
        capacity,
        seatsTaken: taken,
        seatsRemaining: capacity - taken,
        experience,
    };
});

export const typeLabels = {
    showing: "Private Showing",
    roundtable: "Roundtable",
    gala: "Gala",
    briefing: "Briefing",
    summit: "Forum",
    tasting: "Tasting",
    auction: "Auction Preview",
    symposium: "Symposium",
};

export const formatEventDate = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
    });
};
